/**
 * Compact “add context” control for Person hero pills (mobile-first).
 * Frosted horizontal type strip; prefers opening above the [+] control.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { PILL_TYPE_CYCLE, PILL_TYPE_META } from '../../utils/personSearchPills';
import {
  HERO_PILL_MENU_OPTION,
  HERO_PILL_MENU_OPTION_IDLE,
  HERO_PILL_MENU_SHELL,
  placeHeroPillMenu,
} from '../../utils/heroPillMenuPlacement';

/**
 * @param {{
 *   compact: boolean,
 *   onPickType?: (type: string) => void,
 *   onFocusInput?: () => void,
 * }} props
 */
export default function PersonAddContextControl({
  compact = false,
  onPickType,
  onFocusInput,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, placement: 'below' });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const menuEl = menuRef.current;
      const measuredW = menuEl?.offsetWidth || 228;
      const measuredH = menuEl?.offsetHeight || 44;
      setMenuPos(placeHeroPillMenu(rect, { width: measuredW, height: measuredH, gap: 8 }));
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen((v) => !v);
    onFocusInput?.();
  };

  return (
    <div className="relative shrink-0" data-testid="person-add-context">
      <button
        ref={btnRef}
        type="button"
        aria-label="Add more context"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={handleOpen}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-xs font-bold text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
        data-testid={compact ? 'person-add-context-compact' : 'person-add-context-full'}
      >
        <span aria-hidden>+</span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={listId}
          role="listbox"
          aria-label="Context type"
          data-placement={menuPos.placement}
          data-testid="person-add-context-menu"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 90 }}
          className={HERO_PILL_MENU_SHELL}
        >
          {PILL_TYPE_CYCLE.map((opt) => {
            const meta = PILL_TYPE_META[opt];
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  onPickType?.(opt);
                  onFocusInput?.();
                  setOpen(false);
                }}
                className={`${HERO_PILL_MENU_OPTION} ${HERO_PILL_MENU_OPTION_IDLE}`}
              >
                <span aria-hidden className="text-sm leading-none">
                  {meta.emoji}
                </span>
                <span className="hidden min-[380px]:inline">
                  {meta.label === 'Focus' ? 'Context' : meta.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
